import type { BookingCancelReason, BookingCancelReasonDetails } from '~/types'

export const CANCELLATION_POLICY_OPTIONS = {
  NoDatesSelected: 'NoDatesSelected',
  LessThan8Days: 'LessThan8Days',
  Between8To30Days: 'Between8To30Days',
  MoreThan30Days: 'MoreThan30Days',
} as const
Object.freeze(CANCELLATION_POLICY_OPTIONS)
export type CANCELLATION_POLICY_OPTIONS = (typeof CANCELLATION_POLICY_OPTIONS)[keyof typeof CANCELLATION_POLICY_OPTIONS]

export const DECLINE_REASONS_RENTER = {
  FoundDifferentRv: 'FoundDifferentRv',
  NoLongerNeeded: 'NoLongerNeeded',
  NotReadyToBookYet: 'NotReadyToBookYet',
  CostOfRental: 'CostOfRental',
  RenterOther: 'RenterOther',
} as const
Object.freeze(DECLINE_REASONS_RENTER)
export type DECLINE_REASONS_RENTER = (typeof DECLINE_REASONS_RENTER)[keyof typeof DECLINE_REASONS_RENTER]

export const CANCEL_REASONS_RENTER = {
  PersonalOrFamilyEmergency: 'PersonalOrFamilyEmergency',
  COVID: 'COVID',
  AvailabilityOrChangeOfPlans: 'AvailabilityOrChangeOfPlans',
  LackOfFunds: 'LackOfFunds',
  UnresponsiveOwner: 'UnresponsiveOwner',
  WeatherOrTravelRestrictions: 'WeatherOrTravelRestrictions',
  Other: 'Other',
} as const
Object.freeze(CANCEL_REASONS_RENTER)
export type CANCEL_REASONS_RENTER = (typeof CANCEL_REASONS_RENTER)[keyof typeof CANCEL_REASONS_RENTER]

export type CANCEL_OR_DECLINE_REASONS_OWNER = 'TheGuestWantsToCancel' | Extract<BookingCancelReason, 'ExtenuatingCircumstances' | 'HaveConcernsWithThisRenter' | 'MyRvIsNotAvailableForRentOnTheRequestedDates' | 'MyRvIsNotFitForTheRoad' | 'OwnerOther' | 'ThisRvHasBeenSoldOrWillBeSold'>
export const CANCEL_OR_DECLINE_REASONS_OWNER_MAPPING: Record<CANCEL_OR_DECLINE_REASONS_OWNER, Partial<Record<NonNullable<BookingCancelReasonDetails>, string>>> = {
  MyRvIsNotAvailableForRentOnTheRequestedDates: {
    BookedElsewhere: 'BookedElsewhere',
    PersonalUse: 'PersonalUse',
    AmNotAvailableToReleaseOrReturnRv: 'AmNotAvailableToReleaseOrReturnRv',
    Other: 'Other',
  },
  HaveConcernsWithThisRenter: {
    PreferAnotherRenterOnRvezy: 'PreferAnotherRenterOnRvezy',
    DontFeelComfortableWithThePeopleGoingOnTheTrip: 'DontFeelComfortableWithThePeopleGoingOnTheTrip',
    RenterIsTakingMyRvTooFar: 'RenterIsTakingMyRvTooFar',
    RenterIsTakingMyRvOnRoadThatIsUnfitForRvTravel: 'RenterIsTakingMyRvOnRoadThatIsUnfitForRvTravel',
    RenterIsTakingMyRvToFestivalOrEvent: 'RenterIsTakingMyRvToFestivalOrEvent',
    MinimumRentalDaysNotMet: 'MinimumRentalDaysNotMet',
    NoLongerRentingForTwoDayTrips: 'NoLongerRentingForTwoDayTrips',
    NotComfortableRenterExperience: 'NotComfortableRenterExperience',
    Other: 'Other',
  },
  MyRvIsNotFitForTheRoad: {
    HaveMechanicalIssues: 'HaveMechanicalIssues',
    MyRvWasDamagedOnMyLastRental: 'MyRvWasDamagedOnMyLastRental',
    BrokenWindshield: 'BrokenWindshield',
    BrokenAwning: 'BrokenAwning',
    ExteriorDamage: 'ExteriorDamage',
    Other: 'Other',
  },
  // Not sure why this is not a key accepted by the backend
  TheGuestWantsToCancel: {},
  ThisRvHasBeenSoldOrWillBeSold: {},
  ExtenuatingCircumstances: {
    NaturalDisaster: 'NaturalDisaster',
    MedicalIssues: 'MedicalIssues',
    Other: 'Other',
  },
  OwnerOther: {},
} as const
Object.freeze(CANCEL_OR_DECLINE_REASONS_OWNER_MAPPING)

// Get all nested keys of the CANCEL_OR_DECLINE_REASONS_OWNER_MAPPING object
export type CANCEL_OR_DECLINE_REASONS_OWNER_DETAILS = {
  [K in keyof typeof CANCEL_OR_DECLINE_REASONS_OWNER_MAPPING]:
  keyof typeof CANCEL_OR_DECLINE_REASONS_OWNER_MAPPING[K] extends never
    ? null
    : keyof typeof CANCEL_OR_DECLINE_REASONS_OWNER_MAPPING[K]
}[keyof typeof CANCEL_OR_DECLINE_REASONS_OWNER_MAPPING]

export const DECLINATION_ACTIONS = {
  UPDATE_CALENDAR: 'UPDATE_CALENDAR',
  SYNC_CALENDAR: 'SYNC_CALENDAR',
  CALENDAR_SETTINGS: 'CALENDAR_SETTINGS',
  LEARN_HOST_PROTECTION: 'LEARN_HOST_PROTECTION',
  EVENT_PREFERENCES: 'EVENT_PREFERENCES',
  TOWING_PREFERENCES: 'TOWING_PREFERENCES',
  DRIVING_PREFERENCES: 'DRIVING_PREFERENCES',
  UNPUBLISH: 'UNPUBLISH',
} as const
Object.freeze(DECLINATION_ACTIONS)
export type DECLINATION_ACTIONS = (typeof DECLINATION_ACTIONS)[keyof typeof DECLINATION_ACTIONS]

// These mappings come from Stripe error codes:
// https://docs.stripe.com/declines/codes
export const CARD_FAILURE_MAPPINGS = {
  // contactIssuer
  call_issuer: 'contactIssuer',
  do_not_honor: 'contactIssuer',
  do_not_try_again: 'contactIssuer',
  duplicate_transaction: 'contactIssuer',
  fraudulent: 'contactIssuer',
  generic_decline: 'contactIssuer',
  invalid_account: 'contactIssuer',
  lost_card: 'contactIssuer',
  merchant_blacklist: 'contactIssuer',
  new_account_information_available: 'contactIssuer',
  no_action_taken: 'contactIssuer',
  not_permitted: 'contactIssuer',
  offline_pin_required: 'contactIssuer',
  restricted_card: 'contactIssuer',
  revocation_of_all_authorizations: 'contactIssuer',
  revocation_of_authorization: 'contactIssuer',
  security_violation: 'contactIssuer',
  service_not_allowed: 'contactIssuer',
  stolen_card: 'contactIssuer',
  stop_payment_order: 'contactIssuer',
  transaction_not_allowed: 'contactIssuer',
  approve_with_id: 'contactIssuer',
  issuer_not_available: 'contactIssuer',
  processing_error: 'contactIssuer',
  reenter_transaction: 'contactIssuer',
  try_again_later: 'contactIssuer',
  pickup_card: 'contactIssuer',
  deprecated: 'contactIssuer',

  // tryDifferentCard
  authentication_required: 'tryDifferentCard',
  card_not_supported: 'tryDifferentCard',
  currency_not_supported: 'tryDifferentCard',
  online_or_offline_pin_required: 'tryDifferentCard',
  pin_try_exceeded: 'tryDifferentCard',
  testmode_decline: 'tryDifferentCard',
  incorrect_pin: 'tryDifferentCard',
  invalid_pin: 'tryDifferentCard',

  // limitExceeded
  card_velocity_exceeded: 'limitExceeded',
  invalid_amount: 'limitExceeded',
  withdrawal_count_limit_exceeded: 'limitExceeded',

  // insufficientFunds
  insufficient_funds: 'insufficientFunds',

  // cardExpired
  expired_card: 'cardExpired',

  // wrongCVC
  incorrect_cvc: 'wrongCVC',
  invalid_cvc: 'wrongCVC',

  // wrongNumber
  incorrect_number: 'wrongNumber',
  invalid_number: 'wrongNumber',

  // incorrect_zip
  incorrect_zip: 'incorrectZip',

  // wrongExpiry
  invalid_expiry_month: 'wrongExpiry',
  invalid_expiry_year: 'wrongExpiry',

  // calendar unavailable
  calendar_unavailable: 'calendarUnavailable',
} as const
Object.freeze(CARD_FAILURE_MAPPINGS)
export type CARD_FAILURE_MAPPINGS = (typeof CARD_FAILURE_MAPPINGS)[keyof typeof CARD_FAILURE_MAPPINGS]
